import React, { useMemo, useState } from "react";
import { useTheme } from "../../feature/theme/ThemeProvider";
import { WORLD_MAP_COUNTRIES, getCountryColor } from "./worldMapData";
import "./WorldMap.css";

export const WorldMap = ({ countries = [], onSelectCountry }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const countryStatsMap = useMemo(() => {
    const map = new Map();
    (countries || []).forEach((c) => {
      if (c && c.code) {
        map.set(c.code.toUpperCase(), {
          name: c.name || c.code,
          count: Number(c.count) || 0,
        });
      }
    });
    return map;
  }, [countries]);

  const maxClicks = useMemo(() => {
    let max = 0;
    countryStatsMap.forEach((val) => {
      if (val.count > max) max = val.count;
    });
    return max || 1;
  }, [countryStatsMap]);

  const handleMouseMove = (event, countryDef) => {
    const rect = event.currentTarget.ownerSVGElement?.getBoundingClientRect() || event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const stats = countryStatsMap.get(countryDef.code.toUpperCase());
    setHoveredCountry({
      code: countryDef.code,
      name: stats ? stats.name : countryDef.name,
      count: stats ? stats.count : 0,
    });
    setTooltipPos({ x, y });
  };

  const handleMouseLeave = () => {
    setHoveredCountry(null);
  };

  const totalRepresentedClicks = useMemo(() => {
    let sum = 0;
    countryStatsMap.forEach((v) => (sum += v.count));
    return sum;
  }, [countryStatsMap]);

  return (
    <div className="world-map-container">
      <div className="world-map-svg-wrap">
        <svg
          viewBox="0 0 1000 500"
          className="world-map-svg"
          role="img"
          aria-label={`World click map with ${countryStatsMap.size} active countries`}
        >
          {/* Subtle ocean grid lines */}
          <g stroke={isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"} strokeWidth="0.5">
            <line x1="0" y1="125" x2="1000" y2="125" />
            <line x1="0" y1="250" x2="1000" y2="250" strokeDasharray="4 4" />
            <line x1="0" y1="375" x2="1000" y2="375" />
            <line x1="250" y1="0" x2="250" y2="500" />
            <line x1="500" y1="0" x2="500" y2="500" />
            <line x1="750" y1="0" x2="750" y2="500" />
          </g>

          {/* Render Countries */}
          {WORLD_MAP_COUNTRIES.map((c) => {
            const stats = countryStatsMap.get(c.code.toUpperCase());
            const count = stats ? stats.count : 0;
            const fillColor = getCountryColor(count, maxClicks, isDark);
            const isHovered = hoveredCountry?.code === c.code;

            return (
              <path
                key={c.code}
                d={c.path}
                fill={fillColor}
                className={`world-map-path ${isHovered ? "hovered" : ""}`}
                onMouseMove={(e) => handleMouseMove(e, c)}
                onMouseLeave={handleMouseLeave}
                onClick={() => onSelectCountry && onSelectCountry(c.code)}
              />
            );
          })}
        </svg>

        {hoveredCountry && (
          <div
            className="world-map-tooltip"
            style={{ left: `${tooltipPos.x}px`, top: `${tooltipPos.y}px` }}
          >
            <span className="world-map-tooltip-title">{hoveredCountry.name}</span>
            <span className="world-map-tooltip-clicks">
              {hoveredCountry.count.toLocaleString()} click{hoveredCountry.count === 1 ? "" : "s"}
            </span>
          </div>
        )}
      </div>

      <div className="world-map-legend">
        <span>
          {countryStatsMap.size} countr{countryStatsMap.size === 1 ? "y" : "ies"} · {totalRepresentedClicks.toLocaleString()} total clicks
        </span>
        <div className="world-map-legend-scale">
          <span>Less</span>
          <div className="world-map-legend-bar">
            {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
              <span
                key={i}
                className="world-map-legend-color"
                style={{
                  backgroundColor: getCountryColor(pct * maxClicks, maxClicks, isDark),
                }}
              />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;
